/**
 * Open-canvas export (card #243, fallback 3): draw the distributor's
 * arrangement -- a garment silhouette tinted to the color, with each
 * decoration where they put it -- into a PNG that becomes the color's preview
 * image (uploaded through the same color_previews endpoint as fallback 1).
 *
 * Geometry mirrors the Stage: a 6:7 surface, the garment art inset 16% on
 * every side and contain-fit (Stage.vue _silhouetteArtGeometry /
 * GarmentSilhouette), decoration boxes in Stage percent.
 *
 * WHY A SILHOUETTE, NOT THE SUPPLIER PHOTO: the supplier CDN sends no CORS
 * headers, so drawing its photo would taint the canvas and the export would
 * throw. The silhouette is a bundled same-origin asset. Decoration images are
 * loaded with crossOrigin="anonymous": one that cannot be read cross-origin
 * fails to LOAD (it never taints) and is drawn as a labelled box instead.
 */

const STAGE_ASPECT = 6 / 7
const SILHOUETTE_PAD = 0.16

/**
 * Contain-fit an image of ``imgW`` x ``imgH`` inside ``box``, centered.
 *
 * @param {{x:number,y:number,w:number,h:number}} box
 * @param {number} imgW
 * @param {number} imgH
 * @returns {{x:number,y:number,w:number,h:number}}
 */
export function containRect (box, imgW, imgH) {
  if (!(imgW > 0) || !(imgH > 0)) return { x: box.x, y: box.y, w: box.w, h: box.h }
  const s = Math.min(box.w / imgW, box.h / imgH)
  const w = imgW * s
  const h = imgH * s
  return { x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2, w, h }
}

/**
 * Pure layout of the export.
 *
 * @param {object} args
 * @param {number} args.width - output width in px (height follows 6:7).
 * @param {number} args.artAspect - garment art width / height (its viewBox).
 * @param {Array<{box:{leftPct:number,topPct:number,wPct:number,hPct:number}}>} args.items
 * @returns {{width:number,height:number,garment:object,items:Array}}
 */
export function openCanvasLayout ({ width, artAspect, items }) {
  const height = Math.round(width / STAGE_ASPECT)
  const pw = (1 - 2 * SILHOUETTE_PAD) * width
  const ph = (1 - 2 * SILHOUETTE_PAD) * height
  const aspect = artAspect > 0 ? artAspect : 1
  const fitW = Math.min(pw, ph * aspect)
  const fitH = fitW / aspect
  return {
    width,
    height,
    garment: {
      x: SILHOUETTE_PAD * width + (pw - fitW) / 2,
      y: SILHOUETTE_PAD * height + (ph - fitH) / 2,
      w: fitW,
      h: fitH,
    },
    items: (items ?? []).map((item) => ({
      ...item,
      x: (item.box.leftPct / 100) * width,
      y: (item.box.topPct / 100) * height,
      w: (item.box.wPct / 100) * width,
      h: (item.box.hPct / 100) * height,
    })),
  }
}

function loadImage (src, crossOrigin) {
  return new Promise((resolve) => {
    const img = new Image()
    if (crossOrigin) img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = src
  })
}

// A viewBox-only SVG has no intrinsic size, and some browsers refuse to draw
// one into a canvas. Give it one; a blob: URL stays same-origin.
async function sizedSvgUrl (url, vbW, vbH) {
  const text = await (await fetch(url)).text()
  const sized = /<svg[^>]*\swidth=/.test(text)
    ? text
    : text.replace('<svg', `<svg width="${vbW}" height="${vbH}"`)
  return URL.createObjectURL(new Blob([sized], { type: 'image/svg+xml' }))
}

function drawPlaceholder (ctx, item) {
  ctx.save()
  ctx.setLineDash([12, 8])
  ctx.lineWidth = 3
  ctx.strokeStyle = '#6b7280'
  ctx.fillStyle = 'rgba(31, 41, 55, 0.06)'
  ctx.fillRect(item.x, item.y, item.w, item.h)
  ctx.strokeRect(item.x, item.y, item.w, item.h)
  ctx.fillStyle = '#374151'
  ctx.font = `600 ${Math.max(14, Math.round(item.h * 0.3))}px sans-serif`
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(item.initial || '', item.x + item.w / 2, item.y + item.h / 2)
  ctx.restore()
}

/**
 * Draw the arrangement and resolve a PNG Blob.
 *
 * @param {object} args
 * @param {string} args.silhouetteUrl - bundled garment art (same origin).
 * @param {[number, number]} args.viewBox - the art's viewBox size.
 * @param {string|null} args.hex - garment tint.
 * @param {boolean} args.light - lighter detail blend for a light tint.
 * @param {Array} args.items - ``{box, artworkUrl, initial}``.
 * @param {number} [args.width]
 * @returns {Promise<Blob>}
 */
export async function composeOpenCanvas ({ silhouetteUrl, viewBox, hex, light, items, width = 1200 }) {
  const [vbW, vbH] = viewBox
  const plan = openCanvasLayout({ width, artAspect: vbW / vbH, items })
  const canvas = document.createElement('canvas')
  canvas.width = plan.width
  canvas.height = plan.height
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('This browser cannot draw the canvas.')
  ctx.fillStyle = '#ffffff'
  ctx.fillRect(0, 0, plan.width, plan.height)

  if (silhouetteUrl) {
    const blobUrl = await sizedSvgUrl(silhouetteUrl, vbW, vbH)
    try {
      const art = await loadImage(blobUrl, false)
      if (art) {
        const g = plan.garment
        const tint = document.createElement('canvas')
        tint.width = Math.max(1, Math.round(g.w))
        tint.height = Math.max(1, Math.round(g.h))
        const t = tint.getContext('2d')
        t.drawImage(art, 0, 0, tint.width, tint.height)
        t.globalCompositeOperation = 'source-in'
        t.fillStyle = hex || '#c9d3db'
        t.fillRect(0, 0, tint.width, tint.height)
        ctx.drawImage(tint, g.x, g.y, g.w, g.h)
        ctx.save()
        ctx.globalCompositeOperation = 'multiply'
        ctx.globalAlpha = light ? 0.9 : 0.42
        ctx.drawImage(art, g.x, g.y, g.w, g.h)
        ctx.restore()
      }
    } finally {
      URL.revokeObjectURL(blobUrl)
    }
  }

  for (const item of plan.items) {
    const img = item.artworkUrl ? await loadImage(item.artworkUrl, true) : null
    if (img) {
      const r = containRect(item, img.naturalWidth || img.width, img.naturalHeight || img.height)
      ctx.drawImage(img, r.x, r.y, r.w, r.h)
    } else {
      drawPlaceholder(ctx, item)
    }
  }

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error('The canvas could not be saved.'))),
      'image/png',
    )
  })
}
